<!-- ExplainerScene.vue - compact loop shown on the submit page before a photo is picked
     (was public/explainer.svg: English outlines + 7 emoji PNGs). Same story and tokens as LoopScene:
     your photo goes into the AI, the city gets it, an idea comes back. Vector icons, i18n labels.
     CSS beats by default; ExplainerScene.timeline.js is the GSAP version. -->
<template>
  <svg ref="root" class="scene explainer-scene" :class="{ play, gsap: gsapOn }" viewBox="0 0 383 112" role="img" :aria-label="$t('landing.scene.loopAlt')">
    <path class="s-ink" d="M12 76V36c0-5 4-9 9-9h2" stroke-width="1.8" stroke-linecap="round" />
    <path class="s-ink" d="M21 23l4 4-4 4" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    <text data-anim="share" class="label" x="30" y="31">{{ $t('landing.scene.loopShare') }}</text>

    <!-- photo -> AI -->
    <path class="trail s-ink" d="M196 27h14M240 27h36" />
    <g data-anim="phone" class="phone">
      <rect x="215" y="14" width="18" height="27" rx="4" class="f-surface s-line" stroke-width="2" />
      <circle cx="224" cy="25" r="4.5" class="f-mint" />
      <circle cx="224" cy="25" r="1.6" class="f-surface" />
    </g>
    <circle data-anim="dot-in" class="dot dot-in f-mint" cx="242" cy="27" r="3.6" />

    <g data-anim="hub" class="hub">
      <circle class="f-mint" cx="306" cy="54" r="28" />
      <text class="hub-label" x="306" y="62" text-anchor="middle">{{ $t('landing.scene.ai') }}</text>
    </g>
    <g v-for="(c, i) in chips" :key="c" :data-anim="'chip-' + i" class="chip" :class="'c' + i">
      <rect x="339" :y="18 + i * 26" width="38" height="20" rx="5" class="f-chip" />
      <text class="chip-label" x="358" :y="32 + i * 26" text-anchor="middle">{{ c }}</text>
    </g>

    <!-- AI -> city -> you -->
    <path class="trail s-ink" d="M276 84h-34M210 84h-12" />
    <path class="s-ink" d="M202 79l-5 5 5 5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    <g data-anim="city" class="city">
      <path class="f-steel" d="M214 76l12-8 12 8z" />
      <rect x="215" y="77" width="22" height="2.5" class="f-steel" />
      <rect x="217" y="80.5" width="3" height="10" class="f-steel" /><rect x="224.5" y="80.5" width="3" height="10" class="f-steel" /><rect x="232" y="80.5" width="3" height="10" class="f-steel" />
      <rect x="214" y="91" width="24" height="3" rx="1" class="f-steel" />
    </g>
    <circle data-anim="dot-out" class="dot dot-out f-spark" cx="242" cy="84" r="3.6" />

    <g data-anim="bulb" class="bulb">
      <circle class="glow f-spark" cx="12" cy="88" r="13" />
      <circle class="glass" cx="12" cy="86" r="7" />
      <rect x="8.5" y="93" width="7" height="5" rx="1.5" class="f-steel" />
    </g>
    <text data-anim="improve" class="label" x="30" y="80">
      <tspan x="30">{{ $t('landing.scene.loopImprove1') }}</tspan>
      <tspan x="30" dy="16">{{ $t('landing.scene.loopImprove2') }}</tspan>
    </text>
  </svg>
</template>

<script>
import { useScene } from '@/anim/useScene';
import timeline from './ExplainerScene.timeline';

export default {
  name: 'ExplainerScene',
  setup() {
    return { ...useScene({ timeline, threshold: 0.2 }), chips: ['GPT', 'CR', 'ML'] };
  },
};
</script>

<style scoped>
.scene { display: block; width: 100%; height: auto; overflow: visible; }
.scene * { transform-box: view-box; }
.f-mint { fill: var(--ill-mint); } .f-chip { fill: var(--ill-chip); } .f-steel { fill: var(--ill-steel); } .f-spark { fill: var(--ill-spark); } .f-surface { fill: var(--ill-surface); }
.s-ink { stroke: var(--ill-ink); fill: none; } .s-line { stroke: var(--ill-line); }
.trail { stroke-width: 1.4; stroke-dasharray: 5 5; }
.label { font: 400 12px Poppins, sans-serif; fill: var(--ill-ink); }
.hub-label { font: 500 22px Poppins, sans-serif; fill: #fff; }
.chip-label { font: 500 10px Poppins, sans-serif; fill: #fff; }
.glass { fill: var(--ill-spark); stroke: var(--ill-ink); stroke-width: 1.4; }
.glow { opacity: 0.28; }
.dot { opacity: 0; }

.phone { transform-box: fill-box; transform-origin: 50% 100%; }
.hub { transform-origin: 306px 54px; }
.chip { transform-box: fill-box; transform-origin: 0% 50%; }
.city { transform-box: fill-box; transform-origin: 50% 100%; }
.glow { transform-box: fill-box; transform-origin: center; }
.play .trail { animation: ex-flow 1.6s linear both; }
.play .phone { animation: ex-bump 0.4s ease-out 0.1s both; }
.play .dot-in { animation: ex-in 0.6s ease-in 0.35s both; }
.play .hub { animation: ex-pulse 0.45s ease-out 0.9s both; }
.play .c0 { animation: ex-chip 0.4s cubic-bezier(0.3, 1.6, 0.5, 1) 1s both; }
.play .c1 { animation: ex-chip 0.4s cubic-bezier(0.3, 1.6, 0.5, 1) 1.1s both; }
.play .c2 { animation: ex-chip 0.4s cubic-bezier(0.3, 1.6, 0.5, 1) 1.2s both; }
.play .dot-out { animation: ex-out 0.7s ease-out 1.45s both; }
.play .city { animation: ex-bump 0.4s ease-out 1.75s both; }
.play .glass { animation: ex-glass 0.4s ease-out 2.15s both; }
.play .glow { animation: ex-glow 0.9s ease-out 2.15s both; }
@keyframes ex-flow { from { stroke-dashoffset: 20; } to { stroke-dashoffset: 0; } }
@keyframes ex-bump { 0%, 100% { transform: none; } 50% { transform: translateY(-4px) scale(1.08); } }
@keyframes ex-in { 0% { opacity: 0; transform: none; } 20%, 80% { opacity: 1; } 100% { opacity: 0; transform: translateX(32px); } }
@keyframes ex-out { 0% { opacity: 0; transform: none; } 15%, 85% { opacity: 1; } 100% { opacity: 0; transform: translateX(-44px); } }
@keyframes ex-pulse { 0%, 100% { transform: scale(1); } 45% { transform: scale(1.08); } }
@keyframes ex-chip { from { opacity: 0; transform: translateX(-10px) scale(0.6); } to { opacity: 1; transform: none; } }
@keyframes ex-glass { from { fill: var(--ill-surface); } to { fill: var(--ill-spark); } }
@keyframes ex-glow { 0% { opacity: 0; transform: scale(0.4); } 60% { opacity: 0.5; transform: scale(1.15); } 100% { opacity: 0.28; transform: scale(1); } }
.gsap * { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .play * { animation: none !important; } }
</style>
